import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Gift, Plus, Loader2, Trophy, Users, Shuffle, Pencil, Trash2, ArrowLeft, Crown, Calendar, Link2, UserCheck, Ticket, Search, X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { raffleAPI } from '@/lib/api';
import RaffleFormDialog from './RaffleFormDialog';

const STATUS_STYLE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
  drawn: 'bg-amber-50 text-amber-700 border-amber-200',
};

const RoiTile = ({ label, value, icon }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500">{icon} {label}</div>
    <div className="text-lg font-bold text-slate-900 leading-tight">{value}</div>
  </div>
);

// Raffles workspace (lives in a Leads tab). Lists raffles, create/edit,
// and a detail view with entries + winner draw.
export default function RaffleManager({ canManageDefault = true }) {
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await raffleAPI.list();
      setRaffles(r.data.raffles || []);
    } catch {
      toast.error('Failed to load raffles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <RaffleDetail raffleId={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="raffle-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Gift size={18} className="text-amber-600" /> Raffles
          </h2>
          <p className="text-sm text-slate-500">Capture leads at events with prize draws. Enter leads from here, a lead's page, or the Live Capture kiosk.</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="raffle-new-btn">
          <Plus size={16} className="mr-1.5" /> New Raffle
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : raffles.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-slate-500">
          <Gift size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No raffles yet. Create one and assign agents to start capturing leads at events.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {raffles.map((r) => (
            <button
              key={r.raffle_id}
              onClick={() => setSelectedId(r.raffle_id)}
              className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm hover:border-slate-300 transition"
              data-testid={`raffle-card-${r.raffle_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-900">{r.name}</h3>
                <Badge className={`text-[10px] border capitalize ${STATUS_STYLE[r.status] || ''}`}>{r.status}</Badge>
              </div>
              <p className="text-sm text-amber-700 flex items-center gap-1.5 mt-1"><Trophy size={13} /> {r.prize}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Users size={12} /> {r.entry_count} entered</span>
                <span className="flex items-center gap-1 text-emerald-600"><UserCheck size={12} /> {r.converted_count || 0} converted</span>
                {r.draw_date && <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(r.draw_date).toLocaleDateString()}</span>}
              </div>
              {r.status === 'drawn' && r.winner_name && (
                <p className="mt-2 text-xs font-medium text-amber-800 flex items-center gap-1"><Crown size={12} /> {(r.winners?.length || 1) > 1 ? `${r.winners.length} winners` : `Winner: ${r.winner_name}`}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-1">
                {(r.assigned_agent_names || []).slice(0, 3).map((a) => (
                  <span key={a.npn} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{a.name}</span>
                ))}
                {(r.assigned_agent_names || []).length > 3 && <span className="text-[10px] text-slate-400">+{r.assigned_agent_names.length - 3}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      <RaffleFormDialog open={showForm} onOpenChange={setShowForm} raffle={editing} onDone={load} />
    </div>
  );
}

function RaffleDetail({ raffleId, onBack }) {
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [winnerCount, setWinnerCount] = useState(1);
  const [ticketSearch, setTicketSearch] = useState('');
  const [drawTicket, setDrawTicket] = useState('');
  const [editingPrizeFor, setEditingPrizeFor] = useState(null);
  const [prizeDraft, setPrizeDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await raffleAPI.get(raffleId);
      setRaffle(r.data);
    } catch {
      toast.error('Failed to load raffle');
    } finally {
      setLoading(false);
    }
  }, [raffleId]);

  useEffect(() => { load(); }, [load]);

  const doDraw = async (mode, opts = {}) => {
    setDrawing(true);
    const prevIds = new Set((raffle.winners || []).map((w) => w.lead_id));
    try {
      const r = await raffleAPI.draw(raffleId, mode, opts.lead_id || null, {
        count: opts.count,
        ticket_number: opts.ticket_number,
      });
      setRaffle(r.data);
      const added = (r.data.winners || []).filter((w) => !prevIds.has(w.lead_id));
      if (added.length > 1) toast.success(`${added.length} winners drawn: ${added.map((w) => w.lead_name).join(', ')}`);
      else if (added.length === 1) toast.success(`Winner: ${added[0].lead_name}${added[0].ticket_number ? ` (ticket ${added[0].ticket_number})` : ''}`);
      else toast.success('Draw updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Draw failed');
    } finally {
      setDrawing(false);
    }
  };

  const drawRandom = () => {
    const n = Math.max(1, Math.min(Number(winnerCount) || 1, remaining.length));
    if (!window.confirm(`Pick ${n} random winner${n > 1 ? 's' : ''}?`)) return;
    doDraw('random', { count: n });
  };

  const drawByTicket = () => {
    const t = drawTicket.trim();
    if (!t) { toast.error('Enter a ticket number'); return; }
    doDraw('manual', { ticket_number: t }).then(() => setDrawTicket(''));
  };

  const removeWinner = async (leadId) => {
    try {
      const r = await raffleAPI.removeWinner(raffleId, leadId);
      setRaffle(r.data);
      toast.success('Winner removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove winner');
    }
  };

  const startEditPrize = (w) => { setEditingPrizeFor(w.lead_id); setPrizeDraft(w.prize_label || ''); };

  const savePrize = async (leadId, value) => {
    const prizeLabel = (value !== undefined ? value : prizeDraft).trim();
    try {
      const r = await raffleAPI.setWinnerPrize(raffleId, leadId, prizeLabel);
      setRaffle(r.data);
      setEditingPrizeFor(null);
      toast.success(prizeLabel ? `Prize assigned: ${prizeLabel}` : 'Prize cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update prize');
    }
  };

  const del = async () => {
    if (!window.confirm('Delete this raffle and all its entries?')) return;
    try { await raffleAPI.remove(raffleId); toast.success('Raffle deleted'); onBack(); }
    catch { toast.error('Delete failed'); }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/r/${raffle.public_token}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success('Public link copied — share it at the event'),
      () => toast.error('Copy failed'),
    );
  };

  if (loading || !raffle) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const entries = raffle.entries || [];
  const winners = raffle.winners || [];
  const wonLeadIds = new Set(winners.map((w) => w.lead_id));
  const remaining = entries.filter((e) => !wonLeadIds.has(e.lead_id));
  const search = ticketSearch.trim().toLowerCase();
  const shownEntries = search
    ? entries.filter((e) =>
        (e.ticket_number || '').toLowerCase().includes(search) ||
        (e.lead_name || '').toLowerCase().includes(search))
    : entries;

  return (
    <div className="space-y-4" data-testid="raffle-detail">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="raffle-back">
        <ArrowLeft size={14} /> Back to raffles
      </button>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-heading font-semibold text-slate-900">{raffle.name}</h2>
              <Badge className={`text-[10px] border capitalize ${STATUS_STYLE[raffle.status] || ''}`}>{raffle.status}</Badge>
            </div>
            <p className="text-sm text-amber-700 flex items-center gap-1.5 mt-1"><Trophy size={14} /> {raffle.prize}</p>
            {(raffle.prizes || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="raffle-prize-list-header">
                {raffle.prizes.map((p, i) => (
                  <span key={`${p}-${i}`} className="inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
                    <span className="font-bold">{i + 1}.</span> {p}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-1">
              {(raffle.assigned_agent_names || []).map((a) => (
                <span key={a.npn} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{a.name}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyShareLink} data-testid="raffle-share-btn"><Link2 size={14} className="mr-1" /> Share link</Button>
            <Button variant="outline" size="sm" onClick={() => setShowForm(true)} data-testid="raffle-edit-btn"><Pencil size={14} className="mr-1" /> Edit</Button>
            <Button variant="outline" size="sm" className="text-rose-600" onClick={del} data-testid="raffle-delete-btn"><Trash2 size={14} className="mr-1" /> Delete</Button>
          </div>
        </div>

        {/* ROI metrics */}
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="raffle-roi">
          <RoiTile label="Entered" value={entries.length} icon={<Users size={14} className="text-slate-500" />} />
          <RoiTile label="Converted" value={raffle.converted_count || 0} icon={<UserCheck size={14} className="text-emerald-600" />} />
          <RoiTile label="Conversion" value={entries.length ? `${Math.round(((raffle.converted_count || 0) / entries.length) * 100)}%` : '—'} icon={<Trophy size={14} className="text-amber-500" />} />
        </div>

        {/* Winners (ordered) */}
        {winners.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5" data-testid="raffle-winners-banner">
            <div className="flex items-center gap-1.5 text-amber-900 font-semibold text-sm mb-1.5">
              <Crown size={15} className="text-amber-600" /> {winners.length > 1 ? `${winners.length} Winners` : 'Winner'}
            </div>
            <ol className="space-y-1.5">
              {winners.map((w, i) => (
                <li key={w.lead_id} className="flex items-center gap-2 text-sm flex-wrap" data-testid={`raffle-winner-${w.lead_id}`}>
                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500 text-white text-[11px] font-bold shrink-0">{i + 1}</span>
                  <button className="font-medium text-amber-900 hover:underline" onClick={() => navigate(`/leads/${w.lead_id}`)}>{w.lead_name}</button>
                  {w.ticket_number && <span className="text-[11px] bg-white/70 text-amber-800 rounded px-1.5 py-0.5 flex items-center gap-1"><Ticket size={10} /> {w.ticket_number}</span>}
                  {editingPrizeFor === w.lead_id ? (
                    <span className="flex items-center gap-1 flex-wrap">
                      {(raffle.prizes || []).map((p) => (
                        <button
                          key={p}
                          className={`text-[11px] rounded-full px-2 py-0.5 border ${w.prize_label === p ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'}`}
                          onClick={() => savePrize(w.lead_id, p)}
                          data-testid={`raffle-winner-prize-chip-${w.lead_id}-${p}`}
                        >{p}</button>
                      ))}
                      <Input
                        autoFocus
                        value={prizeDraft}
                        onChange={(e) => setPrizeDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') savePrize(w.lead_id); if (e.key === 'Escape') setEditingPrizeFor(null); }}
                        placeholder="Custom prize"
                        className="h-7 w-32 text-xs"
                        data-testid={`raffle-winner-prize-input-${w.lead_id}`}
                      />
                      <Button size="sm" className="h-7 px-2 text-xs" onClick={() => savePrize(w.lead_id)} data-testid={`raffle-winner-prize-save-${w.lead_id}`}>Save</Button>
                      <button className="text-amber-700 hover:text-slate-900 text-xs" onClick={() => setEditingPrizeFor(null)}><X size={13} /></button>
                    </span>
                  ) : (
                    <button
                      className="inline-flex items-center gap-1 text-[11px] text-amber-800 hover:text-amber-950 rounded px-1.5 py-0.5 border border-amber-200 bg-white/50"
                      onClick={() => startEditPrize(w)}
                      data-testid={`raffle-winner-prize-edit-${w.lead_id}`}
                    >
                      <Gift size={11} /> {w.prize_label || 'Set prize'}
                      <Pencil size={10} className="opacity-60" />
                    </button>
                  )}
                  <button
                    className="ml-auto text-amber-700 hover:text-rose-600 text-xs"
                    onClick={() => removeWinner(w.lead_id)}
                    data-testid={`raffle-winner-remove-${w.lead_id}`}
                    title="Remove winner"
                  ><X size={13} /></button>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Draw controls — available while there are entries not yet won */}
        {remaining.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="raffle-draw-controls">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-slate-500">Winners</label>
              <Input
                type="number"
                min={1}
                max={remaining.length}
                value={winnerCount}
                onChange={(e) => setWinnerCount(e.target.value)}
                className="h-8 w-16"
                data-testid="raffle-winner-count"
              />
              <Button size="sm" onClick={drawRandom} disabled={drawing} data-testid="raffle-draw-random">
                {drawing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Shuffle size={14} className="mr-1.5" />} Pick random
              </Button>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="relative">
                <Ticket size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={drawTicket}
                  onChange={(e) => setDrawTicket(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') drawByTicket(); }}
                  placeholder="Ticket #"
                  className="h-8 w-28 pl-7"
                  data-testid="raffle-draw-ticket-input"
                />
              </div>
              <Button size="sm" variant="outline" onClick={drawByTicket} disabled={drawing} data-testid="raffle-draw-ticket-btn">
                Draw by ticket
              </Button>
            </div>
            <span className="text-xs text-slate-400">or pick manually from the list below</span>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <Users size={15} className="text-slate-500" />
          <span className="font-medium text-slate-800">Entries</span>
          <span className="text-sm text-slate-400">({entries.length})</span>
          <div className="relative ml-auto">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={ticketSearch}
              onChange={(e) => setTicketSearch(e.target.value)}
              placeholder="Search ticket or name"
              className="h-8 w-52 pl-7"
              data-testid="raffle-entry-search"
            />
          </div>
        </div>
        {entries.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">No leads entered yet. Enter leads from a lead's page or the Live Capture kiosk.</p>
        ) : shownEntries.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500" data-testid="raffle-entry-no-match">No entries match "{ticketSearch}".</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[620px]">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-semibold">Lead</th>
                  <th className="px-3 py-2 font-semibold">Ticket</th>
                  <th className="px-3 py-2 font-semibold">Lang</th>
                  <th className="px-3 py-2 font-semibold">Source</th>
                  <th className="px-3 py-2 font-semibold">SMS</th>
                  <th className="px-3 py-2 font-semibold">Entered</th>
                  <th className="px-3 py-2 font-semibold text-right">Winner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shownEntries.map((e) => {
                  const isWinner = wonLeadIds.has(e.lead_id);
                  return (
                    <tr key={e.entry_id} className={isWinner ? 'bg-amber-50' : 'hover:bg-slate-50/70'} data-testid={`raffle-entry-${e.entry_id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">
                        <button className="hover:text-teal-700 flex items-center gap-1" onClick={() => navigate(`/leads/${e.lead_id}`)}>
                          {isWinner && <Crown size={12} className="text-amber-500" />} {e.lead_name}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{e.ticket_number || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 uppercase text-xs text-slate-500">{e.preferred_language}</td>
                      <td className="px-3 py-2 text-slate-600 capitalize">{e.source}</td>
                      <td className="px-3 py-2">{e.sms_sent ? <span className="text-emerald-600 text-xs">Sent</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {isWinner ? (
                          <span className="text-amber-700 text-xs font-medium inline-flex items-center gap-1"><Crown size={12} /> Winner</span>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => doDraw('manual', { lead_id: e.lead_id })} disabled={drawing} data-testid={`raffle-pick-${e.entry_id}`}>
                            <Crown size={12} className="mr-1" /> Pick
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RaffleFormDialog open={showForm} onOpenChange={setShowForm} raffle={raffle} onDone={load} />
    </div>
  );
}
